import { useEffect, useState, useRef, useCallback } from 'react';
import * as styles from './styles/hubspot-form.module.scss';
import Image from '@klv/atoms/image';
import { combineGclidAndGaIdValue } from '@klv/shared/utils/google-tag-manager';
import { generateClassName, getUrlTrackingParams } from '@klv/shared/utils/functions';
import { getStoredTrackingParams } from '@klv/shared/utils/attribution';
import getVisitorGeolocation from '@klv/context/localization/utils';

import type { IHubspotFormAtom } from './types';

const MAX_RETRIES = 20;
const FORM_ID_PREFIX = 'hubspot-form-';

interface IFormField {
  name: string;
  value: string;
}

const getGclidValue = async (urlTrackingParams: Record<string, string | undefined>, storedTrackingParams: Record<string, string>): Promise<string> => {
  const gclid = urlTrackingParams?.gclid || '';
  const googleClientId = await combineGclidAndGaIdValue(gclid);
  const irclickid = urlTrackingParams?.irclickid || storedTrackingParams?.irclickid || '';
  return `${googleClientId}${irclickid ? `:${irclickid}` : ''}`;
};

const getUtmParameters = (): IFormField[] => {
  const storedTrackingParams = getStoredTrackingParams();
  return Object.entries(storedTrackingParams)
    .filter(([_, value]) => value)
    .map(([key, value]) => ({
      name: key,
      value: value as string,
    }));
};

const getGclidField = async (): Promise<IFormField> => {
  const urlTrackingParams = getUrlTrackingParams();
  const storedTrackingParams = getStoredTrackingParams();
  const gclid = await getGclidValue(urlTrackingParams, storedTrackingParams);
  return ({
    name: 'gclid',
    value: gclid,
  });
};

const getConversionData = (): IFormField[] => {
  const fields: IFormField[] = [
    {
      name: 'conversion_page',
      value: window.location.href,
    },
  ];

  const klaviyoPagesVisitCount = sessionStorage.getItem('klaviyoPagesVisitCount');
  if (klaviyoPagesVisitCount) {
    try {
      const pagesArray = JSON.parse(klaviyoPagesVisitCount);
      fields.push({
        name: 'web_pages_visited_prior_to_conversion',
        value: JSON.stringify(pagesArray),
      });
    } catch (error) {
      console.error('Error parsing klaviyoPagesVisitCount:', error);
    }
  }

  return fields;
};

const getLocationData = async (): Promise<IFormField | null> => {
  const locationData = await getVisitorGeolocation();
  if (locationData && typeof locationData === 'object' && 'country' in locationData && locationData.country) {
    return {
      name: 'country_indicated_at_signup',
      value: locationData.country,
    };
  }
  return null;
};

const setFormField = (form: HTMLElement, field: IFormField | null) => {
  if (!field) return;
  const input = form.querySelector(`input[name="${field.name}"]`) as HTMLInputElement;
  if (input) {
    input.value = field.value;
  }
};

const HubspotFormAtom = ({
  hubspotHeaderText,
  hubspotSubheaderText,
  formId,
  image,
  redirectUrl,
  singleLineForm,
}: IHubspotFormAtom) => {
  const uniqueIdRef = useRef<string>(`${FORM_ID_PREFIX}${formId}`);
  const intervalId = useRef<number>(0);
  const retryCount = useRef(0);
  const formCreated = useRef(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [retryAttempt, setRetryAttempt] = useState(0);
  const MAX_FORM_RETRIES = 3;

  // Set mounted state after initial render
  useEffect(() => {
    setIsMounted(true);
  }, []);

  const handleFormError = useCallback(() => {
    if (retryAttempt < MAX_FORM_RETRIES) {
      setRetryAttempt(prev => prev + 1);
      formCreated.current = false;
    } else {
      console.error('Max retry attempts reached for form creation');
    }
  }, [retryAttempt]);

  const createForm = useCallback(() => {
    if (!window.hbspt?.forms?.create) {
      console.error('Hubspot forms API not available');
      return;
    }

    // Ensure the target element exists in the DOM
    const targetElement = document.getElementById(uniqueIdRef.current);
    if (!targetElement) {
      console.error('Target element not found in DOM');
      handleFormError();
      return;
    }

    try {
      window.hbspt.forms.create({
        portalId: process.env.GATSBY_HUBSPOT_TRACKING_ID || '',
        formId: formId,
        region: 'na1',
        target: `#${uniqueIdRef.current}`,
        // @ts-ignore - onFormReady is part of the hubspot create function
        onFormReady: async function (form) {
          try {
            const utmFields = getUtmParameters();
            const gclidField = await getGclidField();
            const conversionFields = getConversionData();
            const locationField = await getLocationData();

            [
              ...utmFields,
              gclidField,
              ...conversionFields,
              locationField,
            ].forEach(field => setFormField(form, field));
          } catch (error) {
            console.error('Error setting form fields:', error);
          }
        },
        onFormSubmitted: function () {
          if (redirectUrl) {
            setTimeout(() => {
              window.location.href = redirectUrl;
            }, 10);
          }
        },
        onFormError: function (error: unknown) {
          console.error('Form creation error:', error);
          handleFormError();
        },
      });
    } catch (error) {
      console.error('Error creating form:', error);
      handleFormError();
    }
  }, [formId, retryAttempt, handleFormError]);

  useEffect(() => {
    if (isLoaded && isMounted && uniqueIdRef.current && formId && !formCreated.current) {
      formCreated.current = true;
      
      // Add a small delay to ensure DOM is ready
      setTimeout(() => {
        createForm();
      }, 100);
    }
  }, [formId, isLoaded, isMounted, createForm]);

  useEffect(() => {
    intervalId.current = window.setInterval(() => {
      if (window.hbspt?.forms?.create) {
        setIsLoaded(true);
        window.clearInterval(intervalId.current);
      }
      retryCount.current += 1;
      if (retryCount.current >= MAX_RETRIES) {
        console.error('Hubspot API not loaded after maximum retries.');
        window.clearInterval(intervalId.current);
      }
    }, 250);

    return () => {
      window.clearInterval(intervalId.current);
    };
  }, []);

  // Verify form exists after creation
  useEffect(() => {
    if (isLoaded && isMounted && formCreated.current) {
      const verifyForm = setInterval(() => {
        const formElement = document.querySelector(`#${uniqueIdRef.current} iframe`);
        if (!formElement && retryAttempt < MAX_FORM_RETRIES) {
          formCreated.current = false;
          createForm();
        } else if (formElement) {
          clearInterval(verifyForm);
        }
      }, 1000);
      return () => clearInterval(verifyForm);
    }
  }, [isLoaded, isMounted, retryAttempt, createForm]);


  return (
    <div className={styles.gridContainer}>
      <div className={singleLineForm ? styles.singleLineForm : ''}>
        {hubspotHeaderText && <h4 className={generateClassName(['h4', 'semibold', styles.headline])}>{hubspotHeaderText}</h4>}
        {hubspotSubheaderText && <h6 className={generateClassName([styles.subHeadline])}>{hubspotSubheaderText}</h6>}
        <div className={styles.hubspotForm} id={uniqueIdRef.current} />
      </div>
      {image?.gatsbyImage && <Image altText={image?.altText} imageData={image?.gatsbyImage} />}
    </div>
  );
};

export default HubspotFormAtom;
